import axios, { AxiosInstance, CreateAxiosDefaults } from 'axios';
import { useMemo } from 'react';
import posthog from 'posthog-js';

import { RequestGatewayHook } from '../../commonTypes';
import { useRequestGateway } from '../../hooks/useRequestGateway';

import { useRequestGatewayContext } from '../../context/useRequestGatewayContext';

import { createSecuredAxiosClient } from './createSecuredAxiosClient';

const COMMON_AXIOS_OPTIONS: CreateAxiosDefaults = {
  headers: {
    'Content-Type': 'application/json',
  },
};

function buildCommonAxiosOptions(
  version?: string,
  source?: string
): CreateAxiosDefaults {
  return {
    ...COMMON_AXIOS_OPTIONS,
    headers: {
      ...COMMON_AXIOS_OPTIONS.headers,
      Version: version,
      Source: source,
      'X-POSTHOG-SESSION-ID': posthog.get_session_id() || '',
      'X-POSTHOG-DISTINCT-ID': posthog.get_distinct_id() || '',
    },
  };
}

function normalizeUrlEnding(url: string): string {
  if (url.endsWith('/')) {
    return url.slice(0, -1);
  }

  return url;
}

function normalizeUrlBeginning(url: string): string {
  if (!url.startsWith('/')) {
    return `/${url}`;
  }

  return url;
}

export const useAxios: RequestGatewayHook<AxiosInstance> = (
  options = { forceUnauthenticated: false }
) => {
  const { refreshTokenUrl, version, source } = useRequestGatewayContext();
  const { isProtectedRoute, baseUrl } = useRequestGateway();

  const commonAxiosOptions = buildCommonAxiosOptions(version, source);

  const axiosClientAuth = useMemo(() => {
    const client = createSecuredAxiosClient({
      axiosOptions: commonAxiosOptions,
      refreshTokenUrl: `${normalizeUrlEnding(baseUrl)}${normalizeUrlBeginning(
        refreshTokenUrl
      )}`,
    });

    // baseURL must be set here, otherwise it will be undefined in react-query's useSuspenseQuery
    client.defaults.baseURL = baseUrl;

    return client;
  }, [baseUrl, refreshTokenUrl, commonAxiosOptions]);

  const axiosClientPublic = useMemo(() => {
    const client = axios.create(commonAxiosOptions);
    // baseURL must be set here, otherwise it will be undefined in react-query's useSuspenseQuery
    client.defaults.baseURL = baseUrl;

    return client;
  }, [baseUrl, commonAxiosOptions]);

  return useMemo(() => {
    if (options.forceUnauthenticated || !isProtectedRoute) {
      return axiosClientPublic;
    }

    return axiosClientAuth;
  }, [
    axiosClientAuth,
    axiosClientPublic,
    isProtectedRoute,
    options.forceUnauthenticated,
  ]);
};
